import type { Experiment } from 'types';
import type { DataTableFormatArgs, Column } from '@/components/DataTable';
import { NavLink } from '@/components/NavLink';
import { Button } from '@/components/ui/button';
import { experimentTypeLabel } from 'definitions';
import { formatDateTime } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { ExternalLink, RotateCcw, Trash2 } from 'lucide-react';

type TestExperimentActions = {
  onDelete: (experiment: Experiment) => void;
  onReset: (experiment: Experiment) => void;
};

const CHIP = 'micro-label rounded-pill inline-flex w-fit border px-2 py-0.5';

/** Running is live, Deployed is the accented end state, Draft reads as a selection. */
const statusChip = (status: string) => {
  if (status === 'Running') return 'border-ok-line text-ok';
  if (status === 'Deployed') return 'border-accent-line text-accent';
  if (status === 'Draft' || status === 'QA') return 'border-sel-line text-sel';
  return 'border-line text-dim';
};

const ROW_ICON_BUTTON = 'text-dim hover:text-foreground hover:bg-hover h-8 w-8 rounded-lg p-0';

export const getTestExperimentColumns = (actions: TestExperimentActions): Column<Experiment>[] => [
  {
    field: 'id' as const,
    label: 'ID',
    sortKey: 'id' as const,
    headerClassName: 'micro-label w-20',
    cellClassName: 'num text-dim',
  },
  {
    field: 'websiteUrl' as const,
    label: 'Website',
    sortKey: 'websiteUrl' as const,
    headerClassName: 'micro-label',
    cellClassName: 'text-dim min-w-0',
    format: ({ value }: DataTableFormatArgs<Experiment>) => (
      <span className="block max-w-[220px] truncate">{(value as string) || '-'}</span>
    ),
  },
  {
    field: 'name' as const,
    label: 'Experiment Name',
    sortKey: 'name' as const,
    headerClassName: 'micro-label',
    cellClassName: 'min-w-0',
    format: ({ item }: DataTableFormatArgs<Experiment>) => (
      <NavLink
        to={`/admin/test-experiments/${item.id}`}
        className="text-foreground flex min-w-0 items-center gap-2 group-hover/tablerow:underline"
      >
        <span className="block max-w-[280px] truncate">{item.name}</span>
        <ExternalLink className="text-dim h-3.5 w-3.5 flex-shrink-0" />
      </NavLink>
    ),
  },
  {
    field: 'type' as const,
    label: 'Type',
    sortKey: 'type' as const,
    headerClassName: 'micro-label w-32',
    cellClassName: 'text-dim',
    format: ({ value }: DataTableFormatArgs<Experiment>) => experimentTypeLabel(value as string),
  },
  {
    field: 'status' as const,
    label: 'Status',
    sortKey: 'status' as const,
    headerClassName: 'micro-label w-28',
    format: ({ value }: DataTableFormatArgs<Experiment>) => (
      <span className={cn(CHIP, statusChip((value as string) || ''))}>{(value as string) || '-'}</span>
    ),
  },
  {
    field: 'createdAt' as const,
    label: 'Created',
    sortKey: 'createdAt' as const,
    headerClassName: 'micro-label w-40',
    cellClassName: 'num text-dim',
    format: ({ value }: DataTableFormatArgs<Experiment>) => formatDateTime(value as number) || '-',
  },
  {
    field: 'createdByUser' as const,
    label: 'Created By',
    headerClassName: 'micro-label w-40',
    cellClassName: 'text-dim',
    format: ({ item }: DataTableFormatArgs<Experiment>) => {
      const { createdByUser } = item;
      return `${createdByUser?.firstName ?? ''} ${createdByUser?.lastName ?? ''}`.trim() || '-';
    },
  },
  {
    label: 'Actions',
    headerClassName: 'micro-label w-32',
    format: ({ item }: DataTableFormatArgs<Experiment>) => (
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          title="Reset"
          className={ROW_ICON_BUTTON}
          onClick={() => actions.onReset(item)}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          title="Delete"
          className={cn(ROW_ICON_BUTTON, 'text-danger hover:text-danger hover:bg-danger-bg')}
          onClick={() => actions.onDelete(item)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ),
  },
];
